import type { LucideIcon } from "lucide-react";
import {
  Plane, HeartPulse, Timer, Coffee, CalendarDays, FileText, ClipboardList, MessageCircle,
  FileCheck, LayoutDashboard, ShieldCheck,
  Clock, MapPin, Navigation, Receipt, Car,
} from "lucide-react";
import { Container, Eyebrow } from "../components/ui";
import { FeatureDetailBlock, DETAIL_BLOCKS } from "../components/FeatureDetail";
import FeatureOverview from "../components/FeatureOverview";
import { FeatureVisual } from "../components/FeatureVisuals";
import {
  MotionProvider, Reveal, PromoBanner, IconGridCta, type CtaGridIcon,
  Button as DSButton, Sparkline, BentoCard, ShieldBadge,
  PageHeading, SectionHeading,
} from "../ds/core";
import { useLocale } from "../i18n/useLocale";
import { cmsImage } from "../i18n/cmsImage";
import { featureLiveForMarket } from "../content/flags";
import { TRIAL_PATH as TRIAL } from "../config/entrypoints";

const U = (id: string) => `https://images.unsplash.com/${id}?w=1000&auto=format&fit=crop&q=80`;

/* Faint white tile grid on the coloured panels (fades downward). */
const GRID_W = {
  backgroundImage:
    "linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)",
  backgroundSize: "64px 64px",
  maskImage: "linear-gradient(to bottom, black, transparent)",
  WebkitMaskImage: "linear-gradient(to bottom, black, transparent)",
} as const;

const BLUE_GRAD = "linear-gradient(to bottom, var(--ds-accent-deep), var(--ds-accent))";
const GOLD_GRAD = "linear-gradient(to bottom, var(--ds-warn), var(--ds-gold))";

type TFn = ReturnType<typeof useLocale>["t"];
type Feat = { icon: LucideIcon; title: string; body: string; soon?: boolean;
  /** Stable visual key — the mini-animation must not depend on the translated title. */
  visual?: string };
type Segment = { icon: LucideIcon; label: string; tone: "blue" | "gold" | "dark"; photo: string; alt: string; feats: Feat[] };

const timeSegments = (t: TFn): Segment[] => [
  {
    icon: Plane, label: t("features:seg.leave.label"), tone: "blue",
    photo: cmsImage(t, "features:seg.leave.photo", U("photo-1521791136064-7986c2920216")), alt: t("features:seg.leave.photoAlt"),
    feats: [
      { icon: Plane, title: t("features:seg.leave.leaveBalances.title"), body: t("features:seg.leave.leaveBalances.body"), visual: "leave" },
      { icon: HeartPulse, title: t("features:seg.leave.sickNotes.title"), body: t("features:seg.leave.sickNotes.body"), visual: "absence" },
    ],
  },
  {
    icon: Timer, label: t("features:seg.overtime.label"), tone: "gold",
    photo: cmsImage(t, "features:seg.overtime.photo", U("photo-1504307651254-35680f356dfd")), alt: t("features:seg.overtime.photoAlt"),
    feats: [
      { icon: Timer, title: t("features:seg.overtime.overtime.title"), body: t("features:seg.overtime.overtime.body"), visual: "overtime" },
      { icon: Coffee, title: t("features:seg.overtime.breaks.title"), body: t("features:seg.overtime.breaks.body"), visual: "breaks" },
      { icon: CalendarDays, title: t("features:seg.overtime.shifts.title"), body: t("features:seg.overtime.shifts.body"), visual: "schedule", soon: true },
    ],
  },
  {
    icon: FileText, label: t("features:seg.docs.label"), tone: "dark",
    photo: cmsImage(t, "features:seg.docs.photo", U("photo-1454165804606-c3d57bc86b40")), alt: t("features:seg.docs.photoAlt"),
    feats: [
      { icon: FileText, title: t("features:seg.docs.documents.title"), body: t("features:seg.docs.documents.body"), visual: "documents" },
      { icon: ClipboardList, title: t("features:seg.docs.officeOps.title"), body: t("features:seg.docs.officeOps.body"), visual: "activity" },
      { icon: MessageCircle, title: t("features:seg.docs.messaging.title"), body: t("features:seg.docs.messaging.body"), visual: "messaging", soon: true },
    ],
  },
];

const platform = (t: TFn): Feat[] => [
  { icon: FileCheck, title: t("features:platform.payroll.title"), body: t("features:platform.payroll.body"), visual: "payroll" },
  { icon: LayoutDashboard, title: t("features:platform.glance.title"), body: t("features:platform.glance.body"), visual: "hours" },
  { icon: ShieldCheck, title: t("features:platform.gdpr.title"), body: t("features:platform.gdpr.body"), visual: "compliance" },
];

const Y = "var(--ds-gold-ink)";
const B = "var(--ds-accent-deep)";
const ctaIcons = (t: TFn): CtaGridIcon[] => [
  { name: t("features:cta.icon.clockin"), Icon: Clock, color: B }, { name: t("features:cta.icon.location"), Icon: MapPin, color: Y },
  { name: t("features:cta.icon.leave"), Icon: Plane, color: B }, { name: t("features:cta.icon.absence"), Icon: HeartPulse, color: Y },
  { name: t("features:cta.icon.overtime"), Icon: Timer, color: B }, { name: t("features:cta.icon.breaks"), Icon: Coffee, color: Y },
  { name: t("features:cta.icon.schedule"), Icon: CalendarDays, color: B }, { name: t("features:cta.icon.mileage"), Icon: Navigation, color: Y },
  { name: t("features:cta.icon.expenses"), Icon: Receipt, color: B }, { name: t("features:cta.icon.vehicles"), Icon: Car, color: Y },
  { name: t("features:cta.icon.dashboard"), Icon: LayoutDashboard, color: B }, { name: t("features:cta.icon.payroll"), Icon: FileCheck, color: Y },
  { name: t("features:cta.icon.gdpr"), Icon: ShieldCheck, color: B },
];

/* ── A Time & operations segment rendered as an asymmetric bento: a tall
      coloured label tile (cycling blue / gold / dark) anchors the left column,
      feature cards (each with its own animated visual) fill the rest, and a
      photo tile breaks the grid so it never reads as a flat, even table. */
function SegmentBento({ seg }: { seg: Segment }) {
  const { t } = useLocale();
  const Icon = seg.icon;
  const grad = seg.tone === "gold" ? GOLD_GRAD : seg.tone === "dark" ? undefined : BLUE_GRAD;
  // 2-feat segment leaves a gap on the bottom row → let the photo span it wide.
  const widePhoto = seg.feats.length === 2;
  return (
    <div className="rounded-3xl border border-line bg-surface p-2">
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 lg:auto-rows-[minmax(236px,auto)]">
        {/* Label tile — coloured, white text, tall on desktop */}
        <Reveal className="flex sm:col-span-2 lg:col-span-1 lg:row-span-2">
          <div
            className={`relative flex h-full min-h-[180px] w-full flex-col justify-between overflow-hidden rounded-2xl p-6 ${seg.tone === "dark" ? "bg-ds-hero" : ""}`}
            style={grad ? { background: grad } : undefined}
          >
            <div className="pointer-events-none absolute inset-0" style={GRID_W} />
            <span className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 text-white">
              <Icon size={20} strokeWidth={2} />
            </span>
            <h3 className="relative mt-4 font-display text-ds-22 font-semibold leading-tight tracking-tight text-white">{seg.label}</h3>
          </div>
        </Reveal>

        {/* Feature cards — animated visual per feature */}
        {seg.feats.map((f, i) => (
          <Reveal key={f.title} delay={0.05 * (i + 1)} className="flex">
            <div className="flex h-full w-full flex-col rounded-2xl bg-bg-subtle p-5">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="font-display text-ds-18 font-semibold text-ds-ink">{f.title}</h4>
                {f.soon && <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-ink-faint shadow-sm">{t("features:badge.comingSoon")}</span>}
              </div>
              <p className="mb-5 mt-2 text-ds-14 leading-relaxed text-ds-ink-soft">{f.body}</p>
              <FeatureVisual visual={f.visual} title={f.title} />
            </div>
          </Reveal>
        ))}

        {/* Photo tile — woven into the grid to break the even bento */}
        <Reveal delay={0.05 * (seg.feats.length + 1)} className={`flex ${widePhoto ? "sm:col-span-2 lg:col-span-2" : ""}`}>
          <div className="relative w-full min-h-[200px] overflow-hidden rounded-2xl">
            <img src={seg.photo} alt={seg.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
          </div>
        </Reveal>
      </div>
    </div>
  );
}

/* ── "On every plan" rendered as a bento with the same DS BentoCard tiles the
      home "From legal obligation" band uses: quiet light cards (payroll rows,
      dimensional shield) around one bright-blue live-overview card, plus a dark
      statement tile for the Belgian CIAO@work note. */
const cardBody = "mt-2 text-ds-14 leading-relaxed text-ds-ink-soft";

function PlatformBento() {
  const { market, t } = useLocale();
  const PLATFORM = platform(t);
  return (
    <section id="platform" className="scroll-mt-24 bg-bg-subtle font-ds leading-[1.24]">
      <Container className="py-16 md:py-20">
        <SectionHeading eyebrow={t("features:platform.eyebrow")} title={t("features:platform.title")} intro={t("features:platform.intro")} />
        <MotionProvider>
          <div className="mt-10 grid grid-cols-1 gap-3 lg:grid-cols-3">
            {/* Payroll export — light card, rows ticking green */}
            <Reveal className="flex">
              <BentoCard title={PLATFORM[0].title}>
                <p className={cardBody}>{PLATFORM[0].body}</p>
                <div className="mt-5 flex flex-1 items-end"><FeatureVisual visual={PLATFORM[0].visual} title={PLATFORM[0].title} /></div>
              </BentoCard>
            </Reveal>

            {/* Everything at a glance — bright blue card with a glass live chart */}
            <Reveal delay={0.06} className="flex">
              <BentoCard className="bg-ds-accent">
                <h3 className="text-ds-22 font-semibold tracking-tight text-white">{PLATFORM[1].title}</h3>
                <p className="mt-2 text-ds-14 leading-relaxed text-white/85">{PLATFORM[1].body}</p>
                <div className="mt-4 flex flex-1 items-end">
                  {/* No backdrop-blur: at 90% white it showed a tenth of what was behind it — measured,
                    removing it changes 0.03% of pixels — while an endless animation inside it made
                    Chrome re-blur every frame (DIT-553). */}
                  <div className="w-full rounded-2xl border border-white/60 bg-white/90 p-3 shadow-[0_18px_40px_-18px_rgba(15,23,42,0.4)]">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-ds-pos/15 px-2.5 py-1 text-ds-14 font-semibold text-ds-pos">
                        <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ds-pos opacity-75" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-ds-pos" /></span>
                        {t("features:platform.live")}
                      </span>
                      <span className="rounded-full bg-ds-ink px-2.5 py-1 text-ds-14 font-semibold text-white">328h</span>
                    </div>
                    <Sparkline className="mt-2" data={[40, 46, 44, 58, 52, 70, 64, 80, 74]} highlight={7} />
                    <div className="mt-1 flex justify-between text-ds-14 font-medium text-ds-ink-soft/70">
                      {[t("features:platform.day.mon"), t("features:platform.day.tue"), t("features:platform.day.wed"), t("features:platform.day.thu"), t("features:platform.day.fri")].map((d) => <span key={d}>{d}</span>)}
                    </div>
                  </div>
                </div>
              </BentoCard>
            </Reveal>

            {/* Secure & GDPR — light card, dimensional shield */}
            <Reveal delay={0.12} className="flex">
              <BentoCard title={PLATFORM[2].title}>
                <p className={cardBody}>{PLATFORM[2].body}</p>
                <div className="flex flex-1 items-center justify-center py-2"><ShieldBadge /></div>
              </BentoCard>
            </Reveal>

            {/* Belgium-only CIAO@work tile, gated by the `check_in_at_work` kill-switch */}
            {market === "belgium" && featureLiveForMarket(market, "check_in_at_work") && (
              <Reveal delay={0.16} className="flex lg:col-span-3">
                {/* w-full: the tile is a flex item in the Reveal wrapper, so without
                    it the card shrinks to its content (the max-w-2xl copy + button)
                    and stops short of the three cards above. */}
                <BentoCard dark className="w-full md:flex-row md:items-center md:justify-between md:gap-8">
                  <p className="max-w-2xl text-ds-14 leading-relaxed text-white/75">
                    <span className="font-semibold text-white">{t("features:platform.ciao.q")}</span> {t("features:platform.ciao.body")}
                  </p>
                  <div className="mt-4 shrink-0 md:mt-0"><DSButton to="/project-management" variant="solid-light">{t("features:platform.ciao.cta")}</DSButton></div>
                </BentoCard>
              </Reveal>
            )}
          </div>
        </MotionProvider>
      </Container>
    </section>
  );
}

export default function FeaturesNew() {
  const { market, t } = useLocale();
  return (
    <>
      {/* 1 ── Blue hero */}
      <PageHeading
        eyebrow={t("features:hero.eyebrow")}
        title={t("features:hero.title")}
        intro={t("features:hero.intro")}
        actions={<>
          <DSButton to={TRIAL} variant="solid-light">{t("features:hero.cta.trial")}</DSButton>
          <DSButton to="/pricing" variant="ghost">{t("features:hero.cta.pricing")}</DSButton>
        </>}
      />

      {/* 2 ── Time & operations — the big feature card */}
      <section id="time" className="scroll-mt-24 bg-bg font-ds">
        <Container className="py-16 md:py-20">
          <FeatureDetailBlock block={DETAIL_BLOCKS[0]} />
        </Container>
      </section>

      {/* 3 ── More in Time & operations — three bentos */}
      <MotionProvider>
        <section className="bg-bg font-ds leading-[1.24]">
          <Container className="py-16 md:py-20">
            <SectionHeading
              eyebrow={t("features:more.eyebrow")}
              title={t("features:more.title")}
              intro={t("features:more.intro")}
            />
            {/* Sticky-stacking deck — each bento pins a little lower than the
                last so they pile up, the previous panel's top edge peeking. */}
            <div className="mt-10 flex flex-col gap-4">
              {timeSegments(t).map((seg, i) => (
                <div key={seg.label} className="lg:sticky" style={{ top: `${88 + i * 18}px`, zIndex: i + 1 }}>
                  <SegmentBento seg={seg} />
                </div>
              ))}
            </div>
          </Container>
        </section>
      </MotionProvider>

      {/* 4 ── Blue CTA */}
      <PromoBanner
        eyebrow={t("features:promo1.eyebrow")}
        title={t("features:promo1.title")}
        body={t("features:promo1.body")}
        action={
          <div className="flex flex-wrap items-center justify-center gap-3">
            <DSButton to={TRIAL} variant="solid-light">{t("features:promo1.cta.trial")}</DSButton>
            <DSButton to="/pricing" variant="ghost">{t("features:promo1.cta.pricing")}</DSButton>
          </div>
        }
      />

      {/* 5 ── Vehicles & expenses — the second big feature card */}
      <section id="vehicles" className="scroll-mt-24 bg-bg font-ds">
        <Container className="py-16 md:py-20">
          <FeatureDetailBlock block={DETAIL_BLOCKS[1]} />
        </Container>
      </section>

      {/* 7 ── The big blue all-features block (moved here from the top) */}
      <FeatureOverview detailHref="" />

      {/* 8 ── On every plan — bento */}
      <PlatformBento />

      {/* 7 ── Project & cost control — blue cross-link CTA (shared PromoBanner) */}
      <PromoBanner
        eyebrow={t("features:promo2.eyebrow")}
        title={t("features:promo2.title")}
        body={t("features:promo2.body")}
        action={<DSButton to="/project-management" variant="solid-light">{t("features:promo2.cta")}</DSButton>}
      />

      {/* 10 ── General closing CTA */}
      <section className="bg-bg font-ds">
        <IconGridCta
          items={ctaIcons(t)}
          eyebrow={<Eyebrow>{t("features:closing.eyebrow")}</Eyebrow>}
          title={t("features:closing.title")}
          body={market === "belgium"
            ? t("features:closing.body.be")
            : t("features:closing.body.intl")}
          actions={
            <div className="flex flex-wrap items-center justify-center gap-3">
              <DSButton to={TRIAL} variant="solid">{t("features:closing.cta.trial")}</DSButton>
              <DSButton to="/pricing" variant="outline">{t("features:closing.cta.pricing")}</DSButton>
            </div>
          }
        />
      </section>
    </>
  );
}

